import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImportData = ({ token }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls', 'pdf'].includes(fileExtension)) {
        setFile(selectedFile);
        setResult(null);
      } else {
        toast.error('Please select a CSV, Excel, or PDF file');
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    // Check if it's a PDF - PDFs need longer timeout due to AI processing
    const isPDF = file.name.toLowerCase().endsWith('.pdf');
    const timeoutMs = isPDF ? 120000 : 60000; // 2 min for PDF, 1 min for others

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/transactions/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: timeoutMs,
        }
      );

      setResult({ success: true, data: response.data });
      toast.success(response.data.message);
      setFile(null);
      document.getElementById('file-input').value = null;
    } catch (error) {
      // Extract detailed error message
      let errorMessage = 'Upload failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. PDF processing may take longer - please try again or use a smaller file.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResult({ success: false, error: errorMessage });
      toast.error(errorMessage);
      console.error('Import error details:', error.response?.data || error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
          Import Data
        </h1>
        <p className="mt-1 text-base font-body text-muted-foreground">
          Upload CSV, Excel, or PDF bank statements to import transactions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-8 bg-card border border-border rounded-sm shadow-sm p-8">
          <div className="space-y-6">
            <div>
              <Label htmlFor="file-input" className="font-body text-sm font-medium">
                Select File
              </Label>
              <div className="mt-2">
                <input
                  id="file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  onChange={handleFileChange}
                  className="block w-full text-sm font-body text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border file:border-border file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                  data-testid="file-input"
                />
              </div>
              {file && (
                <div className="mt-3 flex items-center gap-2 text-sm font-body text-foreground">
                  <FileText className="h-4 w-4" />
                  {file.name}
                  <span className="text-xs font-mono text-muted-foreground">
                    ({(file.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full md:w-auto"
              data-testid="upload-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload & Import'}
            </Button>

            {result && (
              <div
                className={`p-4 border rounded-sm flex items-start gap-3 ${
                  result.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
                data-testid="import-result"
              >
                {result.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p
                    className={`font-body font-medium ${
                      result.success ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    {result.success ? 'Import Successful' : 'Import Failed'}
                  </p>
                  <p
                    className={`text-sm font-body mt-1 ${
                      result.success ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {result.success
                      ? `${result.data.count} transactions imported successfully`
                      : result.error}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-4 space-y-6">
          <div className="bg-card border border-border rounded-sm shadow-sm p-6">
            <h3 className="text-lg font-heading font-medium mb-3">File Format Requirements</h3>
            <div className="space-y-2 text-sm font-body text-muted-foreground">
              <p><strong>For CSV/Excel files:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="font-mono">Amount</span> (required)</li>
                <li><span className="font-mono">Description</span> (required)</li>
                <li><span className="font-mono">Date</span> (optional - defaults to today)</li>
                <li><span className="font-mono">Type</span> (optional - auto-detected from amount)</li>
                <li><span className="font-mono">Category</span> (optional - AI categorized)</li>
              </ul>
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-sm">
                <p className="text-xs font-body text-purple-900 flex items-center gap-2">
                  <span className="text-lg">📄</span>
                  <strong>PDF Support:</strong> Upload bank statement PDFs and our AI will automatically extract all transactions!
                </p>
              </div>
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                <p className="text-xs font-body text-blue-900 flex items-center gap-2">
                  <span className="text-lg">✨</span>
                  <strong>AI-Powered:</strong> Categories are automatically assigned based on transaction descriptions!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm shadow-sm p-6">
            <h3 className="text-lg font-heading font-medium mb-3">Supported Formats</h3>
            <div className="space-y-2">
              <p className="text-sm font-body text-muted-foreground mb-3">Works with exports from major Australian banks:</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-mono rounded">CSV</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-mono rounded">XLSX</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-mono rounded">XLS</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-mono rounded">PDF</span>
              </div>
            </div>
            <pre className="mt-4 text-xs font-mono bg-muted p-3 rounded-sm overflow-x-auto">
{`Amount,Description
-50.00,Groceries at Coles
1500.00,Salary Payment
-120.00,Electricity Bill`}
            </pre>
          </div>

          <div className="bg-card border border-border rounded-sm shadow-sm p-6">
            <h3 className="text-lg font-heading font-medium mb-3">Bank Integration</h3>
            <p className="text-sm font-body text-muted-foreground mb-4">
              Automated bank feeds for NetBank and Westpac coming soon.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 border border-border rounded-sm">
                <img
                  src="https://www.netbank.com.au/assets/images/logo.svg"
                  alt="NetBank"
                  className="h-6"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="text-sm font-body font-medium" style={{ display: 'none' }}>
                  NetBank
                </span>
              </div>
              <div className="flex items-center gap-2 p-2 border border-border rounded-sm">
                <img
                  src="https://www.westpac.com.au/etc/designs/responsive-wesforms/clientlibs/img/westpac-logo.svg"
                  alt="Westpac"
                  className="h-6"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="text-sm font-body font-medium" style={{ display: 'none' }}>
                  Westpac
                </span>
              </div>
            </div>
            <p className="text-xs font-mono text-muted-foreground mt-3 uppercase tracking-wider">
              Status: Coming Soon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportData;
